import type { JSONContent } from "@tiptap/react";
import {
  EMPTY_NOTE_DOCUMENT,
  sanitizeNoteDocument,
  type NoteBlock,
  type NoteDocument,
  type NoteListItem,
  type NoteText,
} from "@verity-connect/domain";

/**
 * Between what the editor emits and what is stored for years.
 *
 * These are two vocabularies on purpose. The editor's shape is whatever its library
 * happens to use today and will change when that library does; the stored shape is a
 * small set of things three renderers — screen, plain text, and soon a PDF — have all
 * agreed to draw. Translating here means a change of editor is a change to this file,
 * not a migration of every teacher's notes.
 *
 * Everything unrecognised is dropped on the way in. The domain sanitiser has the last
 * word regardless, so nothing here is the only guard.
 */

export function toNoteDocument(value: JSONContent | undefined): NoteDocument {
  if (!value) return EMPTY_NOTE_DOCUMENT;
  const document = sanitizeNoteDocument({
    type: "doc",
    content: (value.content ?? []).map(toBlock).filter(Boolean),
  });
  // The editor is given a paragraph to hold its placeholder. That paragraph is the
  // editor's, not hers, so a note holding nothing else is still an empty note. Blank
  // paragraphs inside real writing are left alone: she put them there.
  return isOnlyAnEmptyParagraph(document) ? EMPTY_NOTE_DOCUMENT : document;
}

function isOnlyAnEmptyParagraph(document: NoteDocument): boolean {
  const only = document.content.length === 1 ? document.content[0] : undefined;
  return only?.type === "paragraph" && only.content.length === 0;
}

function toBlock(node: JSONContent): unknown {
  switch (node.type) {
    case "paragraph":
      return { type: "paragraph", content: toInline(node.content) };
    case "heading":
      return { type: "heading", level: node.attrs?.level ?? 2, content: toInline(node.content) };
    case "blockquote":
      return { type: "quote", content: toInline(flatten(node.content)) };
    case "codeBlock":
      return { type: "codeBlock", text: plain(node.content) };
    case "bulletList":
      return { type: "bulletList", items: toItems(node.content) };
    case "orderedList":
      return { type: "orderedList", items: toItems(node.content) };
    case "taskList":
      return {
        type: "taskList",
        items: (node.content ?? []).map((item) => ({
          content: toInline(flatten(item.content)),
          done: item.attrs?.checked === true,
        })),
      };
    case "horizontalRule":
      return { type: "divider" };
    default:
      return null;
  }
}

/** A list item holds paragraphs and possibly a nested list; both matter. */
function toItems(content: JSONContent[] | undefined): unknown[] {
  return (content ?? []).map((item) => {
    const nested = (item.content ?? []).find(
      (child) => child.type === "bulletList" || child.type === "orderedList",
    );
    const own = (item.content ?? []).filter((child) => child !== nested);
    const result: { content: unknown; children?: unknown } = { content: toInline(flatten(own)) };
    if (nested) result.children = toItems(nested.content);
    return result;
  });
}

function flatten(content: JSONContent[] | undefined): JSONContent[] {
  return (content ?? []).flatMap((node) => (node.type === "text" ? [node] : node.content ?? []));
}

function toInline(content: JSONContent[] | undefined): unknown[] {
  return (content ?? [])
    .filter((node) => node.type === "text" && typeof node.text === "string")
    .map((node) => {
      const marks = (node.marks ?? []).map((mark) => mark.type);
      const link = (node.marks ?? []).find((mark) => mark.type === "link");
      return {
        type: "text",
        text: node.text,
        marks: marks.filter((mark) => mark !== "link"),
        ...(link?.attrs?.href ? { href: link.attrs.href } : {}),
      };
    });
}

function plain(content: JSONContent[] | undefined): string {
  return (content ?? []).map((node) => node.text ?? "").join("");
}

/** And back, so a stored note opens in the editor as she left it. */
export function toEditorContent(document: NoteDocument): JSONContent {
  // An empty note has no blocks, but an editor needs one: the placeholder hangs off a
  // paragraph, and without it a teacher opening her notes sees a blank box that says
  // nothing about what it is for. The editor repairs itself the moment she taps, which
  // is too late to have told her.
  const content = document.content.map(fromBlock);
  return { type: "doc", content: content.length > 0 ? content : [{ type: "paragraph" }] };
}

function fromBlock(block: NoteBlock): JSONContent {
  switch (block.type) {
    case "paragraph":
      return { type: "paragraph", content: fromInline(block.content) };
    case "heading":
      return { type: "heading", attrs: { level: block.level }, content: fromInline(block.content) };
    case "quote":
      return { type: "blockquote", content: [{ type: "paragraph", content: fromInline(block.content) }] };
    case "codeBlock":
      return { type: "codeBlock", content: block.text ? [{ type: "text", text: block.text }] : [] };
    case "bulletList":
      return { type: "bulletList", content: fromItems(block.items, "bulletList") };
    case "orderedList":
      return { type: "orderedList", content: fromItems(block.items, "orderedList") };
    case "taskList":
      return {
        type: "taskList",
        content: block.items.map((item) => ({
          type: "taskItem",
          attrs: { checked: item.done },
          content: [{ type: "paragraph", content: fromInline(item.content) }],
        })),
      };
    case "divider":
      return { type: "horizontalRule" };
  }
}

function fromItems(items: readonly NoteListItem[], listType: string): JSONContent[] {
  return items.map((item) => {
    const content: JSONContent[] = [{ type: "paragraph", content: fromInline(item.content) }];
    if (item.children?.length) content.push({ type: listType, content: fromItems(item.children, listType) });
    return { type: "listItem", content };
  });
}

function fromInline(content: readonly NoteText[]): JSONContent[] {
  return content.map((run) => {
    const marks = (run.marks ?? []).map((mark) => ({ type: mark }));
    if (run.href) marks.push({ type: "link", attrs: { href: run.href } } as never);
    return { type: "text", text: run.text, ...(marks.length ? { marks } : {}) };
  });
}
